import { useState, useEffect } from "react";
import {
  Package,
  X,
  Loader2,
  RefreshCw,
  Home,
  Map,
  Settings,
  BarChart3,
  Shield,
  Wrench,
  ToggleLeft,
  ToggleRight,
  Info,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import Swal from "sweetalert2";

// Mapa de iconos por slug del módulo
const iconMap = {
  principal: Home,
  operaciones: Map,
  gestion: Settings,
  reportes: BarChart3,
  sistema: Shield,
  mantenimiento: Wrench,
};

const ModulosEmpresa = ({ empresa, onClose, onModulosActualizados }) => {
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null); // id del módulo que está haciendo toggle

  const getApiUrl = () =>
    process.env.REACT_APP_API_URL || "http://localhost:8000/api";

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  useEffect(() => {
    if (empresa?.id) cargarModulos();
  }, [empresa]);

  const cargarModulos = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/empresas/${empresa.id}/modulos`,
        { headers: getHeaders() },
      );

      if (response.ok) {
        const data = await response.json();
        setModulos(data);
      } else {
        throw new Error("Error al cargar módulos");
      }
    } catch (error) {
      console.error("Error:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Error al cargar módulos",
        showConfirmButton: false,
        timer: 2500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (modulo) => {
    setToggling(modulo.id);
    try {
      const response = await fetch(
        `${getApiUrl()}/empresas/${empresa.id}/modulos/toggle`,
        {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify({ menu_id: modulo.id }),
        },
      );

      if (response.ok) {
        const data = await response.json();

        // Actualizar estado local inmediatamente (sin recargar)
        setModulos((prev) =>
          prev.map((m) =>
            m.id === modulo.id ? { ...m, activo: data.activo } : m,
          ),
        );

        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: data.message,
          showConfirmButton: false,
          timer: 1500,
        });

        // Notificar al padre si necesita actualizar su vista
        if (onModulosActualizados) onModulosActualizados();
      } else {
        throw new Error("Error al actualizar módulo");
      }
    } catch (error) {
      console.error("Error:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "No se pudo actualizar el módulo",
        showConfirmButton: false,
        timer: 2500,
      });
    } finally {
      setToggling(null);
    }
  };

  const modulosActivos = modulos.filter((m) => m.activo).length;
  const modulosTotal = modulos.length;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3 backdrop-blur-sm">
      <Card className="w-full max-w-lg overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <CardHeader className="pb-2 pt-3 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-indigo-600 rounded-lg">
                <Package className="h-4 w-4 text-white" />
              </div>
              <div>
                <CardTitle className="text-sm font-semibold">
                  Módulos — {empresa.nombre}
                </CardTitle>
                <p className="text-xs text-gray-500 mt-0.5">
                  {modulosActivos} de {modulosTotal} módulos habilitados
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={cargarModulos}
                disabled={loading}
                className="h-7 text-xs"
              >
                <RefreshCw
                  className={`h-3 w-3 ${loading ? "animate-spin" : ""}`}
                />
              </Button>
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4">
          {/* Banner informativo */}
          <div className="flex items-start gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <Info className="h-3.5 w-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-800">
              Los módulos habilitados aquí son el{" "}
              <strong>techo de acceso</strong> para el Admin Local de{" "}
              <strong>{empresa.nombre}</strong>. Los módulos deshabilitados no
              aparecerán en su sidebar.
            </p>
          </div>

          {/* Lista de módulos */}
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
              <span className="ml-2 text-xs text-gray-600">
                Cargando módulos...
              </span>
            </div>
          ) : modulos.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p className="text-xs">No hay módulos disponibles</p>
            </div>
          ) : (
            <div className="space-y-2">
              {modulos.map((modulo) => {
                const IconComponent = iconMap[modulo.slug] || Package;
                const isToggling = toggling === modulo.id;

                return (
                  <div
                    key={modulo.id}
                    className={`
                      flex items-center justify-between p-3 rounded-lg border transition-all
                      ${
                        modulo.activo
                          ? "border-green-200 bg-green-50/60"
                          : "border-gray-200 bg-gray-50/60"
                      }
                    `}
                  >
                    {/* Icono + Nombre */}
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-2 rounded-lg ${
                          modulo.activo
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        <IconComponent className="h-4 w-4" />
                      </div>
                      <div>
                        <p
                          className={`text-sm font-medium ${
                            modulo.activo ? "text-gray-900" : "text-gray-400"
                          }`}
                        >
                          {modulo.name}
                        </p>
                        <p className="text-xs text-gray-400 font-mono">
                          {modulo.slug}
                        </p>
                      </div>
                    </div>

                    {/* Badge + Toggle */}
                    <div className="flex items-center gap-3">
                      <Badge
                        className={`text-xs ${
                          modulo.activo
                            ? "bg-green-100 text-green-700 border-green-200"
                            : "bg-gray-100 text-gray-500 border-gray-200"
                        }`}
                      >
                        {modulo.activo ? "Habilitado" : "Deshabilitado"}
                      </Badge>

                      <button
                        type="button"
                        onClick={() => handleToggle(modulo)}
                        disabled={isToggling}
                        className="transition-opacity disabled:opacity-50"
                        title={
                          modulo.activo
                            ? "Deshabilitar módulo"
                            : "Habilitar módulo"
                        }
                      >
                        {isToggling ? (
                          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                        ) : modulo.activo ? (
                          <ToggleRight className="h-6 w-6 text-green-600 hover:text-green-700" />
                        ) : (
                          <ToggleLeft className="h-6 w-6 text-gray-400 hover:text-gray-600" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end mt-4 pt-3 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="h-7 text-xs"
            >
              Cerrar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModulosEmpresa;
